<template>
    <div class="sf-community-page">
      <div class="sf-community-shell" v-if="state === 'view'">
        <a href="/" class="sf-back">← Explore communities</a>

        <div class="sf-notice" v-if="favoriteSignup" role="status">
          <a href="/accounts/signup">Sign up</a> to save this community.
          <button @click="favoriteSignup = false" type="button">
            Dismiss
          </button>
        </div>

        <div class="sf-notice" v-if="community.status === 'draft'">
          This community is a draft and is not publicly listed.
        </div>


<div v-if="editable" class="sf-owner-toolbar">
  <div>
    <strong>You manage this community</strong>
    <span>You're viewing its visitor page.</span>
  </div>
  <a :href="'/#/community-manager/' + slug + '/overview'">
    <EditIcon /> Open community manager →
  </a>
</div>


        <header class="sf-community-heading">
          <p class="sf-eyebrow">{{ community.type || 'Community' }}</p>
          <h1>{{ community.title }}</h1>
          <p class="sf-location">
            {{ [community.city, community.state].filter(Boolean).join(', ') }}
          </p>
          <p v-if="community.university_name" class="sf-campus">
            {{ community.university_name }}
          </p>
          <a href="#visit-details" class="sf-visit-jump">
          View visit details ↓
        </a>
      </header>

        <section
          v-if="community.images && community.images.length"
          class="sf-community-gallery"
          aria-label="Community photos"
        >
          <img
            class="sf-main-photo"
            :src="community.images[selectedImg].compressed_img ||
                  community.images[selectedImg].thumbnail || no_img"
            :alt="community.title + ' community photo'"
          />

          <div class="sf-photo-options" v-if="community.images.length > 1">
            <button
              v-for="(image, index) in community.images"
              :key="index"
              type="button"
              :class="{ selected: selectedImg === index }"
              :aria-label="'View photo ' + (index + 1)"
              :aria-pressed="selectedImg === index"
              @click="selectImg(index)"
            >
              <img
                :src="image.thumbnail || image.compressed_img || no_img"
                alt=""
                loading="lazy"
              />
            </button>
          </div>
        </section>

        <div class="sf-community-layout">
          <main class="sf-community-content">
            <p v-if="community.small_description" class="sf-introduction">
              {{ community.small_description }}
            </p>

            <div class="sf-community-actions">
              <button type="button" class="sf-secondary" @click="favorite">
                {{ favorited ? 'Saved community' : 'Save community' }}
              </button>
              <button type="button" class="sf-text-button" @click="share">
                Share with a friend ↗
              </button>
            </div>

            <div v-if="shareDialog" class="sf-share-panel">
              <label for="community-url">Community link</label>
              <input
                id="community-url"
                class="form-control"
                type="text"
                :value="'https://storyfaith.com/' + community.slug + '/'"
                readonly
              />
              <button type="button" class="sf-secondary" @click="copyLink">
                Copy link
              </button>
              <button
                type="button"
                class="sf-text-button"
                @click="shareDialog = false"
              >
                Close
              </button>
            </div>

            <section v-if="community.content" class="sf-detail-section">
              <p class="sf-eyebrow">GET TO KNOW US</p>
              <h2>About this community</h2>
              <p class="sf-community-description">{{ community.content }}</p>
            </section>

            <section
              v-if="community.services && community.services.length"
              class="sf-detail-section"
            >
              <h2>What to expect</h2>
              <div class="sf-service-list">
                <div
                  class="sf-service"
                  v-for="(service, index) in community.services"
                  :key="index"
                >
                  <img
                    v-if="service.image_icon"
                    :src="service.image_icon"
                    alt=""
                  />
                  <span>{{ service.title }}</span>
                </div>
              </div>
            </section>

            <section
              v-if="community.location"
              class="sf-detail-section"
            >
              <h2>Find us</h2>
              <p v-if="community.address">{{ community.address }}</p>
              <div id="map-canvas" class="sf-community-map"></div>
            </section>

            <section
              v-if="community.events && community.events.length"
              class="sf-detail-section"
            >
              <Events :events="community.events" />
            </section>

            <section
              v-if="community.website || community.instagram_link ||
                    community.facebook_link || community.twitter_link"
              class="sf-detail-section"
            >
              <h2>More from this community</h2>
              <div class="sf-community-links">
                <a
                  v-if="community.website"
                  :href="community.website"
                  target="_blank"
                  rel="noopener noreferrer"
                >Website ↗</a>
                <a
                  v-if="community.instagram_link"
                  :href="community.instagram_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >Instagram ↗</a>
                <a
                  v-if="community.facebook_link"
                  :href="community.facebook_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >Facebook ↗</a>
                <a
                  v-if="community.twitter_link"
                  :href="community.twitter_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >Twitter ↗</a>
              </div>
            </section>
          </main>

          <aside class="sf-community-sidebar">
            <section id="visit-details" class="sf-visit-card">
              <p class="sf-eyebrow">COME SAY HELLO</p>
              <h2>Your first visit</h2>
              <p class="sf-visit-intro">
                Find a gathering and take your next step.
              </p>

              <div class="sf-visit-detail">
                <h3>When we meet</h3>
                <p v-if="community.schedule">{{ community.schedule }}</p>
                <p v-else>Check with the community for current meeting times.</p>
              </div>

              <div class="sf-visit-detail">
                <h3>Where we meet</h3>
                <p v-if="community.address">{{ community.address }}</p>
                <p v-else>Contact the community to confirm the gathering location.</p>
              </div>

              <a
                v-if="community.website"
                :href="community.website"
                class="sf-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit community website ↗
              </a>
            </section>

            <section v-if="hasVisibleHost" class="sf-host-panel">
              <Connect
                v-if="hostCardContent === 'connect'"
                :username="username"
                :connect-result="connectResult"
                :host="community.host"
                @close="connectClose"
                @send="connectSend"
              />
              <HostCard
                v-else
                :community="community"
                :username="username"
                @connect="hostCardContent = 'connect'"
              />
            </section>
          </aside>
        </div>
      </div>
    </div>
  </template>
<script>
// Fetch compatibility
import 'whatwg-fetch'
import EditIcon from "../components/icons/EditIcon"
import Events from './CommunityEvents'
import Connect from './Connect'
import HostCard from './HostCard'
import { getCookie, settings } from '../client'

const csrftoken = getCookie('csrftoken'); csrftoken;
const mapboxgl = window.mapboxgl;
if (mapboxgl)
    mapboxgl.accessToken = settings['MAPBOXGL_TOKEN'];

function renderMap(community) {
    if (!mapboxgl || !community.location) return;

    let coords = community.location.split(",");
    let latitude = parseFloat(coords[0]);
    let longitude = parseFloat(coords[1]);
    if (!document.getElementById("map-canvas")) return;
    var map = new mapboxgl.Map({
        container: 'map-canvas',
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [longitude, latitude],
        zoom: 14
    });

    var marker1 = new mapboxgl.Marker({ color: 'black' })
        .setLngLat([longitude, latitude]);
    marker1.addTo(map);
}

export default {
    name: 'CommunityGallery',
    props: {
        community_id: String,
        slug: String,
        username: String,
        editable: Boolean
    },
    components: {
        Events, Connect, HostCard, EditIcon
    },
    data () {
        return {
            no_img: '/static/communities/gallery-no-img.png',
            state: 'view',
            editorErrors: null,
            selectedImg: 0,
            community: {
                images: null,
                title: "Community",
                host: null,
                services: [],
                events: []
            },
            servicesList: [],
            favorited: false,
            shareDialog: false,
            hostCardContent: 'host',
            connectResult: null,
            favoriteSignup: null
        };
    },
    computed: {
        hasVisibleHost () {
            const host = this.community.host;
            return Boolean(
                host &&
                !this.community.hide_host &&
                host.username !== 'storyfaith-web-test'
            );
        },

        images () {
            var imagelist = [];
            if (this.community.images) {
                for(let i = 0; i<this.community.images.length; i++) {
                    imagelist.push(this.community.images[i]);
                }
            }
            if (imagelist.length < 4) {
                for (let i = imagelist.length; i<4; i++) {
                    imagelist.push({
                        url: '/static/communities/gallery-no-img.png',
                        thumbnail: '/static/communities/gallery-no-img.png'
                    });
                }
            }
            return imagelist;
        },
        servicesAvailable () {
            const self = this;
            return this.servicesList.filter( function (item) {
                return ! self.community.services.some(function (service) {
                    return service.id == item.id;
                });
            } );
        }
    },
    methods: {
        async connectSend (formData) {
            if (this.connectResult && this.connectResult.state === 'pending') return;
            this.connectResult = {state: 'pending'};
            const url = this.username
                ? '/api/account/' + this.username + '/connectcommunity/'
                : '/api/community/' + this.slug + '/connect/';
            try {
                const response = await fetch(url, {
                    headers: {'Content-Type': 'application/json', 'X-CSRFToken': csrftoken},
                    body: JSON.stringify({...formData, community: this.slug}),
                    method: 'POST'
                });
                const data = await response.json();
                this.connectResult = response.ok && data.ok === true
                    ? {ok: true}
                    : {errors: data.errors || {}, error: 'We couldn’t send your message. Please check your details and try again.'};
            } catch (error) {
                this.connectResult = {error: 'We couldn’t send your message. Please try again. Your details are still here.'};
            }
        },
        connectClose () {
          this.hostCardContent = "host";
          this.connectResult = null;
        },
        updateModel () {
          let self = this;
          fetch("/api/community/" + this.slug + "/").then(async function(result) {
            self.community = await result.json();
            self.$emit("updateModel")
          });
        },
        selectImg(i) {
          this.selectedImg = i;
        },
        favorite () {
            if (!this.username) {
                this.notifySignup();
                return;
            }
            var self = this;
            var data = new FormData();
            data.append("community", this.community.slug);
            fetch("/api/account/" + this.username + "/favoritecommunity/", {
                method: "POST",
                headers: {
                    "X-CSRFToken": csrftoken
                },
                body: data
            }).then(async function (result) {
                if (result.status == 200) {
                    self.favorited = !self.favorited;
                }
            });
        },
        share () {
            this.shareDialog = true;
        },
        copyLink () {
            var inputField = document.querySelector('#community-url');
            inputField.removeAttribute("disabled");
            inputField.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                alert("Couldn't copy link");
            }
            inputField.setAttribute("disabled", "disabled");
            window.getSelection().removeAllRanges();
        },
        notifySignup () {
            this.favoriteSignup = true;
        },
    },
    created () {
        var self = this;
        this.updateModel();

        if (this.username) {
            fetch("/api/account/" + this.username + "/favoritecommunity/?community=" + this.slug,  {
                method: "GET",
                headers: {
                    "X-CSRFToken": csrftoken,
                    'Cache-Control': 'no-cache'
                }
            }).then(async function(result) {
                self.favorited = await result.json();
            });
        }
    },
    watch: {
        community (val) {
            this.$nextTick(() => {
                renderMap(val);
            });
        },
        state (val) {
            var self = this;
            this.$nextTick(() => {
                if (val === "view") {
                    renderMap(self.community);
                }
            })
        }
    },
    mounted () {
    }
}
</script>
<style>
.sf-community-page {
  background: #faf8f2;
  color: #26362e;
  font-family: 'Open Sans', sans-serif;
  padding: 28px 24px 72px;
}

.sf-community-shell {
  max-width: 1180px;
  margin: 0 auto;
}

.sf-community-page a {
  color: #26362e;
}

.sf-back,
.sf-manage {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  text-decoration: none;
  margin-bottom: 24px;
}

.sf-manage {
  margin-left: 20px;
}

.sf-community-heading {
  margin: 8px 0 28px;
}

.sf-community-page .sf-eyebrow {
  margin: 0 0 12px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .15em;
  text-transform: uppercase;
  color: #62695f;
}

.sf-community-page h1,
.sf-community-page h2 {
  font-family: Georgia, serif;
  font-weight: 400;
  text-align: left;
  color: #26362e;
}

.sf-community-page h1 {
  margin: 0 0 14px;
  font-size: clamp(32px, 4vw, 52px);
  line-height: 1.15;
}

.sf-community-page h2 {
  margin: 0 0 18px;
  font-size: 28px;
  line-height: 1.25;
}

.sf-community-page h3 {
  margin: 0 0 8px;
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  text-align: left;
}

.sf-community-page p {
  font-size: 14px;
  line-height: 1.8;
}

.sf-location,
.sf-campus {
  margin: 0;
  color: #62695f;
}

.sf-community-gallery {
  margin-bottom: 36px;
}

.sf-main-photo {
  display: block;
  width: 100%;
  height: clamp(220px, 32vw, 380px);
  object-fit: cover;
  background: #efece2;
  border-radius: 18px;
}

.sf-photo-options {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 12px 2px;
}

.sf-photo-options button {
  flex: 0 0 96px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 10px;
  overflow: hidden;
  background: transparent;
  cursor: pointer;
}

.sf-photo-options button.selected {
  border-color: #ffd400;
}

.sf-photo-options img {
  display: block;
  width: 100%;
  height: 66px;
  object-fit: cover;
}

.sf-community-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 64px;
  align-items: start;
}

.sf-community-content {
  min-width: 0;
}

.sf-community-page .sf-introduction {
  margin: 0 0 20px;
  font-size: 18px;
  line-height: 1.7;
}

.sf-community-actions,
.sf-community-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.sf-community-actions {
  margin-bottom: 32px;
}

.sf-community-page .sf-primary,
.sf-community-page .sf-secondary,
.sf-community-page .sf-text-button {
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  cursor: pointer;
  text-decoration: none;
}

.sf-community-page .sf-primary {
  display: block;
  padding: 14px 18px;
  border: 0;
  border-radius: 10px;
  background: #ffd400;
  color: #26362e;
  text-align: center;
}

.sf-community-page .sf-secondary {
  padding: 10px 16px;
  border: 1px solid #dddccf;
  border-radius: 9px;
  background: #fff;
  color: #26362e;
}

.sf-community-page .sf-text-button {
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: #26362e;
}

.sf-detail-section {
  padding: 30px 0;
  border-top: 1px solid #e5e3d8;
}

.sf-community-description,
.sf-visit-detail p {
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.sf-community-sidebar {
  display: grid;
  gap: 18px;
}

.sf-visit-card,
.sf-host-panel {
  padding: 26px;
  border: 1px solid #e5e3d8;
  border-radius: 16px;
  background: #fff;
}

.sf-visit-intro {
  color: #62695f;
}

.sf-visit-detail {
  padding: 18px 0;
  border-top: 1px solid #efeee7;
}

.sf-visit-detail p {
  margin: 0;
  color: #62695f;
}

.sf-service-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.sf-service {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border: 1px solid #e5e3d8;
  border-radius: 10px;
  font-size: 13px;
}

.sf-service img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.sf-community-map {
  width: 100%;
  height: 240px;
  border-radius: 14px;
  background: #eaeedc;
}

.sf-community-links a {
  font-size: 13px;
}

.sf-notice,
.sf-share-panel {
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #efe7d4;
}

.sf-share-panel input {
  margin: 10px 0;
}

.sf-community-page button:focus-visible,
.sf-community-page a:focus-visible {
  outline: 3px solid #7b8659;
  outline-offset: 4px;
}

@media (max-width: 800px) {
  .sf-community-page {
    padding: 24px 18px 48px;
  }

  .sf-community-layout {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .sf-community-sidebar {
    grid-row: 1;
  }

  .sf-service-list {
    grid-template-columns: 1fr;
  }
}

/* Community page navigation and gallery refinements */
#sf-community-detail {
  background: #faf8f2;
}

#sf-community-detail > header,
#sf-community-detail #header {
  background: #faf8f2 !important;
  border-bottom: 1px solid #e5e3d8;
}

#sf-community-detail #header {
  border-bottom: 0;
}

#sf-community-detail #header .nav-link,
#sf-community-detail #header .navbar-brand,
#sf-community-detail #header .navbar-brand-alt {
  color: #26362e !important;
}

#sf-community-detail #header .navbar-toggler {
  background: #26362e;
}

#sf-community-detail > footer {
  background: #efece2 !important;
  color: #26362e !important;
  border-top: 1px solid #e5e3d8;
}

#sf-community-detail > footer a,
#sf-community-detail > footer h3,
#sf-community-detail > footer p {
  color: #26362e !important;
}

.sf-community-page .sf-visit-jump {
  display: inline-block;
  margin-top: 18px;
  font-size: 13px;
  font-weight: 700;
  text-underline-offset: 4px;
}

.sf-community-page #visit-details {
  scroll-margin-top: 24px;
}

.sf-community-page .sf-host-panel:empty {
  display: none;
}


/* Responsive community layout refinement */
.sf-community-page,
.sf-community-page * {
  box-sizing: border-box;
}

.sf-community-page .sf-community-shell {
  width: 100%;
  min-width: 0;
}

.sf-community-page .sf-community-layout {
  grid-template-columns: minmax(0, 1fr) minmax(260px, 340px);
  gap: clamp(24px, 4vw, 64px);
}

.sf-community-page .sf-community-sidebar,
.sf-community-page .sf-visit-card,
.sf-community-page .sf-host-panel {
  min-width: 0;
  max-width: 100%;
}

.sf-community-page .sf-community-sidebar p,
.sf-community-page .sf-community-sidebar a {
  overflow-wrap: anywhere;
  white-space: normal;
}

.sf-community-page .sf-visit-detail p {
  white-space: pre-line;
}

.sf-community-page .sf-community-map {
  max-width: 100%;
}

@media (max-width: 950px) {
  .sf-community-page .sf-community-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
  }

  .sf-community-page .sf-community-sidebar {
    grid-row: 1;
  }
}


/* Matching discovery navigation */
#sf-community-detail .sf-detail-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  padding: 22px max(24px, calc((100vw - 1180px) / 2));
  background: #faf8f2 !important;
  border-bottom: 1px solid #e5e3d8;
  font-family: 'Open Sans', sans-serif;
}

#sf-community-detail .sf-detail-brand img {
  display: block;
  width: 145px;
  height: auto;
}

#sf-community-detail .sf-detail-nav nav {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

#sf-community-detail .sf-detail-nav > a,
#sf-community-detail .sf-detail-nav nav a,
#sf-community-detail .sf-detail-footer a {
  color: #26362e;
  font-size: 13px;
  text-decoration: none;
}

#sf-community-detail .sf-detail-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  padding: 30px max(24px, calc((100vw - 1180px) / 2));
  background: #efece2 !important;
  font-family: 'Open Sans', sans-serif;
}

#sf-community-detail .sf-detail-footer p {
  flex: 1 1 260px;
  margin: 0;
  font-size: 13px;
  color: #62695f;
}

@media (max-width: 600px) {
  #sf-community-detail .sf-detail-nav {
    padding: 18px;
  }

  #sf-community-detail .sf-detail-nav nav {
    gap: 16px;
  }

  #sf-community-detail .sf-detail-footer {
    padding: 28px 18px;
  }
}


/* StoryFaith charcoal framing */
#sf-community-detail .sf-detail-nav,
#sf-community-detail .sf-detail-footer {
  background: #222222 !important;
  color: #faf8f2 !important;
  border-color: #333333 !important;
}

#sf-community-detail .sf-detail-nav > a,
#sf-community-detail .sf-detail-nav nav a,
#sf-community-detail .sf-detail-footer a,
#sf-community-detail .sf-detail-footer p {
  color: #faf8f2 !important;
}


.sf-owner-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  gap: 20px; padding: 18px 20px; margin: 20px 0 30px;
  border: 1px solid #d7dacd; border-radius: 12px; background: #eaeedc;
}
.sf-owner-toolbar strong, .sf-owner-toolbar span { display: block; font-size: 13px; }
.sf-owner-toolbar span { margin-top: 5px; color: #62695f; font-size: 12px; }
.sf-owner-toolbar a {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px; color: #26362e; font-weight: 700;
}
@media (max-width: 600px) {
  .sf-owner-toolbar { align-items: flex-start; flex-direction: column; }
}

</style>
